/**
 * `EmailMailboxNav` — the mailbox switcher for the email surface
 * (inbox / sent / spam / … as the runtime reports them), with per-mailbox
 * counters.
 *
 * A HORIZONTAL TAB ROW at every density (operator direction 2026-07-22). It was
 * a left-hand column, which cost ~144px of width permanently to hold three
 * short labels — width the MESSAGE LIST needs far more, since that is where
 * truncation actually hurts. Folders are few and rarely switched; the list is
 * read constantly. Tabs spend the space where the eye spends its time.
 *
 * Deliberately DATA-DRIVEN: it renders exactly what `mail.mailboxes` returns
 * rather than a hardcoded folder set, so the day the runtime adds Trash/Drafts
 * (or counts) they appear with no SPA change. Icons are matched by canonical id
 * with a neutral fallback, so an unknown folder still renders sensibly.
 *
 * Counters: the badge shows the folder's TOTAL message count (`mail.mailboxes`
 * ships `total`+`unread` since `4403de2`). Total, not unread, on purpose — this
 * is a read-only MONITORING view of the agent's mail, where the lawyer's
 * question is "how many emails are here", and the number must MATCH the list
 * they see. An unread badge showed "2" beside a 5-message Sent folder, which
 * reads as a bug (and "unread sent mail" is not a concept). The badge is omitted
 * when the count is 0 or absent — never rendered as "0", which would assert an
 * emptiness on a runtime that does not report counts at all.
 */
import { useTranslation } from "react-i18next";
import { FileEdit, Inbox, Mail, Send, ShieldAlert, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { Mailbox } from "@/api/lx-mail";
import { cn } from "@/lib/utils";

/** Canonical id → glyph. Unknown folders fall back to a generic envelope. */
const MAILBOX_ICONS: Record<string, LucideIcon> = {
  inbox: Inbox,
  sent: Send,
  spam: ShieldAlert,
  trash: Trash2,
  drafts: FileEdit,
};

/**
 * Each mailbox carries its own hue, matching the accent vocabulary the section
 * cards above use (`AGENT_SECTIONS`) so the two strips read as one system.
 * Hues are semantic rather than decorative — incoming, outgoing, suspect,
 * discarded, unfinished — so a lawyer finds the folder by colour before
 * reading the label.
 *
 * The icon keeps its hue even when idle; only the SELECTED tab gets the filled
 * treatment. That way the strip is legible as a set at a glance, and the
 * current folder is still unambiguous.
 */
interface MailboxAccent {
  idle: string;
  active: string;
  activeText: string;
  badge: string;
}

const NEUTRAL_ACCENT: MailboxAccent = {
  idle: "text-slate-400 dark:text-cream-500/80",
  active: "border-cream-400 bg-cream-100 dark:border-coffee-600 dark:bg-coffee-800",
  activeText: "text-coffee-800 dark:text-cream-100",
  badge: "bg-cream-200/80 text-coffee-700 dark:bg-coffee-700 dark:text-cream-200",
};

const MAILBOX_ACCENTS: Record<string, MailboxAccent> = {
  inbox: {
    idle: "text-sky-500/90 dark:text-sky-300/85",
    active: "border-sky-400/70 bg-sky-50 dark:border-sky-400/50 dark:bg-sky-950/40",
    activeText: "text-sky-700 dark:text-sky-200",
    badge: "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200",
  },
  sent: {
    idle: "text-emerald-500/90 dark:text-emerald-300/85",
    active:
      "border-emerald-400/70 bg-emerald-50 dark:border-emerald-400/50 dark:bg-emerald-950/40",
    activeText: "text-emerald-700 dark:text-emerald-200",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200",
  },
  spam: {
    idle: "text-amber-500/90 dark:text-amber-300/85",
    active: "border-amber-400/70 bg-amber-50 dark:border-amber-400/50 dark:bg-amber-950/40",
    activeText: "text-amber-700 dark:text-amber-200",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200",
  },
  trash: {
    idle: "text-rose-500/90 dark:text-rose-300/85",
    active: "border-rose-400/70 bg-rose-50 dark:border-rose-400/50 dark:bg-rose-950/40",
    activeText: "text-rose-700 dark:text-rose-200",
    badge: "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-200",
  },
  drafts: {
    idle: "text-violet-500/90 dark:text-violet-300/85",
    active: "border-violet-400/70 bg-violet-50 dark:border-violet-400/50 dark:bg-violet-950/40",
    activeText: "text-violet-700 dark:text-violet-200",
    badge: "bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-200",
  },
};

export function EmailMailboxNav({
  mailboxes,
  active,
  onSelect,
}: {
  mailboxes: Mailbox[];
  active: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="tablist"
      aria-label={t("agent.channels.email.mailboxesLabel")}
      data-testid="email-mailbox-nav"
      data-shape="tabs"
      className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-cream-200/60 px-2 py-1.5 dark:border-coffee-800/60"
    >
      {mailboxes.map((mb) => (
        <MailboxButton key={mb.id} mb={mb} selected={mb.id === active} onSelect={onSelect} />
      ))}
    </div>
  );
}

function MailboxButton({
  mb,
  selected,
  onSelect,
}: {
  mb: Mailbox;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const Icon = MAILBOX_ICONS[mb.id] ?? Mail;
  const accent = MAILBOX_ACCENTS[mb.id] ?? NEUTRAL_ACCENT;
  // TOTAL message count, not unread — the monitoring-surface metric that matches
  // the list (see the header note). Omitted at 0/absent so an empty folder does
  // not assert "0" on a runtime that reports no counts.
  const count = typeof mb.total === "number" && mb.total > 0 ? mb.total : null;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      data-testid="email-mailbox-tab"
      data-mailbox-id={mb.id}
      onClick={() => onSelect(mb.id)}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-body-sm leading-tight transition-colors",
        selected
          ? cn("font-medium", accent.active, accent.activeText)
          : "border-transparent text-coffee-600 hover:bg-cream-100 dark:text-cream-400 dark:hover:bg-coffee-800/60",
      )}
    >
      <Icon size={14} className={cn("shrink-0", !selected && accent.idle)} aria-hidden />
      <span className="truncate">{mb.name}</span>
      {count !== null && (
        <span
          data-testid="email-mailbox-count"
          className={cn(
            "shrink-0 rounded px-1.5 text-[11px] font-medium tabular-nums",
            accent.badge,
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
